import { useCreateIntegrationField } from '@modules/integrations/hooks/use-create-integration-field';
import { useDeleteIntegrationField } from '@modules/integrations/hooks/use-delete-integration-field';
import { useGetIntegrationInfo } from '@modules/integrations/hooks/use-get-integration-info';
import { PlusBackground } from '@shared/assets/icons/components';
import { Button, Flex, Spin } from 'antd';
import { useCallback, useState, type FC } from 'react';
import { Text } from '@shared/ui/antd';
import styles from './integrations-item-fields.module.scss';
import { useAgentFieldOptions } from './hooks/use-agent-field-options';
import { useIntegrationFieldsDraft } from './hooks/use-integration-fields-draft';
import { IntegrationFieldRow } from './ui/integration-field-row/integration-field-row';
import { useTranslation } from 'react-i18next';
import { ScrollContainer } from '@shared/ui/scroll-container';
import type { WebhookMethodType } from '@shared/api';
import {
  getIntegrationFieldPresetByValue,
  INTEGRATION_FIELD_PRESET_URLS,
  INTEGRATION_FIELD_PRESET_VALUES,
} from '@modules/integrations/lib/integration-field-presets';

interface IntegrationsItemFieldsProps {
  integrationId: number;
  isOpen: boolean;
  requestMethod?: WebhookMethodType;
}

export const IntegrationsItemFields: FC<IntegrationsItemFieldsProps> = ({
  integrationId,
  isOpen,
  requestMethod,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'integrations' });
  const { integrationInfo, isLoading, isError } = useGetIntegrationInfo(integrationId, isOpen);

  const { createIntegrationField } = useCreateIntegrationField();
  const { deleteIntegrationField } = useDeleteIntegrationField();

  const { options: agentFieldOptions } = useAgentFieldOptions(integrationInfo?.channel_id);

  const { draftFields, busyLocalIds, updateRow, addRow, saveRow, deleteRow } =
    useIntegrationFieldsDraft({
      integrationId,
      integrationInfo,
      createIntegrationField,
      deleteIntegrationField,
    });
  const [openSelectLocalId, setOpenSelectLocalId] = useState<string | null>(null);

  const handleAddRow = useCallback(() => {
    addRow();
  }, [addRow]);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ height: '100%', padding: '20px 0' }}>
        <Spin />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex align="center" justify="center" style={{ height: '100%', padding: '20px 0' }}>
        <Text variant="sm" fontWeight={600}>
          {t('fieldsError')}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex vertical className={styles.root} gap={12}>
      <ScrollContainer maxHeight="700px" height="auto" style={{ flex: '0 1 auto' }}>
        <Flex vertical gap={12}>
          {draftFields.map((field) => {
            const isRowBusy = busyLocalIds.includes(field.localId);
            const isPersisted = Boolean(field.id);
            const isRowDisabled = isRowBusy || isPersisted;
            const hasDefaultValue = Boolean(field.value?.trim());
            const hasAgentField = field.agentFieldId !== null;

            const isSaveDisabled =
              isRowBusy ||
              isPersisted ||
              !field.parameterName.trim() ||
              (!hasAgentField && !hasDefaultValue);

            return (
              <IntegrationFieldRow
                key={field.localId}
                field={field}
                requestMethod={requestMethod ?? integrationInfo?.method ?? 'POST'}
                isBusy={isRowBusy}
                isPersisted={isPersisted}
                isDisabled={isRowDisabled}
                isSaveDisabled={isSaveDisabled}
                agentFieldOptions={agentFieldOptions}
                isSelectOpen={!isRowDisabled && openSelectLocalId === field.localId}
                onSelectOpenChange={(open) => {
                  if (isRowDisabled) return;
                  if (open) {
                    setOpenSelectLocalId(field.localId);
                    return;
                  }
                  setOpenSelectLocalId((prev) => (prev === field.localId ? null : prev));
                }}
                onChangeParameterName={(value) =>
                  updateRow(field.localId, { parameterName: value })
                }
                onChangeAgentField={(value) => {
                  if (value === INTEGRATION_FIELD_PRESET_VALUES.iframeLink) {
                    updateRow(field.localId, {
                      agentFieldId: null,
                      value: INTEGRATION_FIELD_PRESET_URLS.iframeLink,
                    });
                    return;
                  }

                  updateRow(field.localId, {
                    agentFieldId: value,
                    value:
                      value === null && getIntegrationFieldPresetByValue(field.value)
                        ? null
                        : field.value,
                  });
                }}
                onChangeLocation={(value) => updateRow(field.localId, { location: value })}
                onChangeCustomValue={(value) => updateRow(field.localId, { value })}
                onSave={() => void saveRow(field.localId)}
                onDelete={() => void deleteRow(field.localId)}
              />
            );
          })}
        </Flex>
      </ScrollContainer>

      <Button
        type="text"
        className={styles.addButton}
        icon={<PlusBackground fill="var(--color-text-secondary)" />}
        iconPosition="end"
        disabled={false}
        onClick={handleAddRow}
      >
        {t('addField')}
      </Button>
    </Flex>
  );
};
